import { Dialog, DialogActions, DialogContent, DialogTitle, Link } from "@mui/material";
import { Button } from "../common/Button";
import React, { useContext, useState } from "react";
import { Checkbox } from "../common/Checkbox";
import { ZahtjevContext } from "../../common/context/ZahtjevProvider";
import { Trans, useTranslation } from "react-i18next";

export type GdprCookieInformation = {
    areBaseCookiesAllowed: boolean;
    areAnalyticsCookiesAllowed: boolean;
    areMarketingCookiesAllowed: boolean;
}

export function CookieConsent() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(true);
    const [areSettingsSelected, setAreSettingsSelected] = useState<boolean>(true);
    const [cookieConsentValues, setCookieConsentValues] = useState<GdprCookieInformation>({
        areBaseCookiesAllowed: true,
        areAnalyticsCookiesAllowed: false,
        areMarketingCookiesAllowed: false,
    });
    const { setGdprCookieData } = useContext(ZahtjevContext);

    const handleAcceptAll = () => {
        const cookieData: GdprCookieInformation = {
            areBaseCookiesAllowed: true,
            areAnalyticsCookiesAllowed: true,
            areMarketingCookiesAllowed: true,
        };
        setGdprCookieData(cookieData);
        setOpen(false);
    };

    const onClick = () => {
        if (areSettingsSelected) {
            setAreSettingsSelected(false);
        } else {
            setGdprCookieData(cookieConsentValues);
            setOpen(false);
        }
    }

    const handleCheckboxValueChange = (name: string) =>
      (value: boolean) => {
          return setCookieConsentValues(prevState => {
              return {
                  ...prevState,
                  [name]: value
              }
          })
      };

    return (
      <Dialog open={open} PaperProps={{ sx: { p: 3, borderRadius: 4, minWidth: 280 } }}>
          <DialogTitle sx={{ fontWeight: 700, fontSize: 24 }}>{t("cookie-preferences.title")}</DialogTitle>
          <DialogContent className="mt-1 mb-5">
              {areSettingsSelected ? (
                  <p><Trans
                    i18nKey="cookie-preferences.description"
                    components={{
                      "main-page": <Link href="https://www.otpbanka.hr" target="_blank" rel="noopener noreferrer"/>,
                      "cookie-preferences-document": <Link href="https://www.otpbanka.hr/pravila-o-kolacicima"
                                                           target="_blank" rel="noopener noreferrer" />
                    }}
                  /></p>
              ) : (
                <>
                    <Checkbox
                      value={cookieConsentValues.areBaseCookiesAllowed}
                      label={t("cookie-preferences.settings.core-cookies-title")}
                      disabled={true}
                      description={t("cookie-preferences.settings.core-cookies-description")}
                      onChange={handleCheckboxValueChange("areBaseCookiesAllowed")}
                    />
                    <Checkbox
                      value={cookieConsentValues.areAnalyticsCookiesAllowed}
                      label={t("cookie-preferences.settings.analytic-cookies-title")}
                      description={t("cookie-preferences.settings.analytic-cookies-description")}
                      onChange={handleCheckboxValueChange("areAnalyticsCookiesAllowed")}
                    />
                    <Checkbox
                      value={cookieConsentValues.areMarketingCookiesAllowed}
                      label={t("cookie-preferences.settings.marketing-cookies-title")}
                      description={t("cookie-preferences.settings.marketing-cookies-description")}
                      onChange={handleCheckboxValueChange("areMarketingCookiesAllowed")}
                    />
                </>
              )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2 }}>
              <Button
                variant="secondary"
                onClick={() => onClick()}
                text={
                    areSettingsSelected
                      ? t("cookie-preferences.button-settings")
                      : t("cookie-preferences.button-accept-selected")
                } />
              <Button onClick={() => handleAcceptAll()} text={t("cookie-preferences.button-accept-all")} />
          </DialogActions>
      </Dialog>
    )
}
